import { usePreferences } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * How much of FlowGate this account needs to see.
 *
 * Two questions decide it, asked once and changeable forever after: does anyone else use this
 * account, and is there more than one WhatsApp number. A "no" to both, which is every
 * organisation in production today, all 74 of them with one member and one environment, has no
 * use for members, roles, environments or a list of organisations, and showing them is four
 * screens of administration in front of somebody who came to build a form.
 *
 * Both default to the simple answer, so nothing has to be answered for the app to be usable, and
 * nothing is taken away from anyone who has not answered: there is no account with a second
 * member or environment to take it from. The only way to acquire one is through a screen behind
 * these flags, so an account cannot strand itself by answering "just me".
 *
 * A demo session is always the simple shape and cannot change it, the server refuses the
 * change too ({@code UserPreferencesService.NOT_FOR_DEMO_USERS}), because every screen behind
 * these answers is account administration for an account the visitor does not have yet.
 */
export function useWorkspaceShape() {
  const { preferences, loading } = usePreferences();
  const { isDemoMode } = useAuth();

  return {
    /** Members and roles: somebody else uses this account. */
    showTeamTools: !isDemoMode && Boolean(preferences.worksWithATeam),
    /** Organisations and environments: more than one WhatsApp number to keep apart. */
    showWorkspaceTools: !isDemoMode && Boolean(preferences.usesSeveralNumbers),
    /** Whether the two questions have been answered or waved away. */
    answered: isDemoMode || Boolean(preferences.workspaceSetupCompleted),
    /** A demo session is stuck with the simple shape; a real account never is. */
    canChange: !isDemoMode,
    loading,
  };
}

export default useWorkspaceShape;
